import { IBlockPack } from "./IBlockPack";
import { IBlockPackFactoryImpl } from "./IBlockPackFactoryImpl";
import DefaultBlockPack from "./DefaultBlockPack";

export type BlockPackType = "default" | "fancy" | "cement" | "glass";

// Default implementation just returns DefaultBlockPack (safe for server)
// This ensures that if setImplementation is never called, we don't crash, 
// but we also don't import heavy assets.
class DefaultFactoryImpl implements IBlockPackFactoryImpl {
    createPack(type: BlockPackType): IBlockPack {
        return new DefaultBlockPack();
    }
}

let factoryImplementation: IBlockPackFactoryImpl = new DefaultFactoryImpl();
const packCache: Map<BlockPackType, IBlockPack> = new Map();

export function setBlockPackFactoryImplementation(impl: IBlockPackFactoryImpl) {
    factoryImplementation = impl;
    packCache.clear(); // Clear cache when implementation changes
}

export function getBlockPack(packType: BlockPackType = "default"): IBlockPack {
  // Return cached pack if available
  if (packCache.has(packType)) {
    return packCache.get(packType)!;
  }

  // Create and cache pack using the injected implementation
  const pack = factoryImplementation.createPack(packType);
  
  packCache.set(packType, pack);
  return pack;
}

export function getBlockPackFromStorage(): IBlockPack {
  try {
    const stored = localStorage.getItem("blockPack");
    let packType = stored ? JSON.parse(stored) : "default";
    // Migrate old "rounded" to "fancy"
    if (packType === "rounded") {
      packType = "fancy";
      localStorage.setItem("blockPack", JSON.stringify("fancy"));
    }
    return getBlockPack(packType as BlockPackType);
  } catch {
    return getBlockPack("default");
  }
}

/**
 * Get block pack for a canvas, using game's blockPack if this is a remote player,
 * otherwise falling back to localStorage (for local players)
 * 
 * For remote players: uses their own block pack preference (from playerState.blockPack) or "default"
 * For local players: uses localStorage preference
 */
export function getBlockPackForCanvas(canvas?: { game?: { isRemote?: boolean; blockPack?: string } }): IBlockPack {
  // Explicitly check if this is a remote player's canvas
  if (canvas?.game?.isRemote) {
    // Remote player: use their own block pack preference, or "default" if not set
    // Never use localStorage for remote players - always use their preference or default
    return getBlockPack((canvas.game.blockPack || "default") as BlockPackType);
  }
  // Local player: use localStorage
  return getBlockPackFromStorage();
}
